import { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { Box, Menu, MenuItem, TextField } from '@material-ui/core';

import services, {
  useAddProductToCartMutation,
  useGetCustomerCartsQuery,
  useGetShoppingCartDetailsQuery,
  useLazyGetShoppingCartDetailsQuery,
  useRemoveProductFromCartMutation,
  useUpdateProductInCartMutation,
} from 'core/services';

import useIsLoading from 'hooks/useIsLoading.hook';
import useMessage from 'hooks/useMessage.hook';
import { getArray, getFunc } from 'utils/helpers';
import Icon from 'components/Icon';
import Button2 from 'components/Button2';
import ModalLogin from 'components/ModalLogin';

import style from './style.module.scss';
import useSkipForProduct from 'hooks/useSkipForProduct.hook';

const { changeCart } = services;

const getProductCount = (cartData, product_id) => {
  const cartProduct = getArray(cartData?.items).find((item) => {
    return item.product.id === product_id;
  });
  return cartProduct?.qty || 0;
};

const AddToCardButton = ({
  buttonProps,
  onRemoveItem: customOnRemoveItem,
  onAddItem: customOnAddItem,
  setCount,
  count: initialCount,
  size = 'large',
  variant = 'contained',
  min,
  max,
  product_id,
  inputField,
  disabled,
  disabledAdd,
  onAfterAdd,
  disabledRemove: legacyDisableRemove,
  setLoginModal: legacySetLoginModal,
  renderAddButton,
  addButtonClassName,
  className,
  containerClassName,
  width,
  ...rest
}) => {
  const m = useMessage();
  const [cardsMenuEl, setCardsMenuEl] = useState(null);
  const [loginModal, setLoginModal] = useState(false);
  const isloggedIn = useSelector((state) => state.authorization.isloggedIn);
  const cart_id = useSelector((state) => state.cart.cart_id);
  const skipForProduct = useSkipForProduct();
  const cartDetailsQuery = useGetShoppingCartDetailsQuery(
    { cart_id },
    {
      skip: skipForProduct || !cart_id || !isloggedIn,
    },
  );
  const [getCartDetails] = useLazyGetShoppingCartDetailsQuery();
  const cartsListQuery = useGetCustomerCartsQuery(undefined, {
    skip: skipForProduct || !isloggedIn,
  });
  const [addProduct, addProductQuery] = useAddProductToCartMutation();
  const [updateProduct] = useUpdateProductInCartMutation();
  const [removeProduct, removeProductQuery] =
    useRemoveProductFromCartMutation();

  const cartDetails = cartDetailsQuery.data;
  const user_permissions = cartDetails?.user_permissions;
  const customerCardsList = getArray(cartsListQuery.data);

  const removeLoading = useIsLoading(removeProductQuery);
  const addLoading = useIsLoading(addProductQuery);

  const count = useMemo(() => {
    if (Number.isFinite(initialCount)) {
      return initialCount;
    }
    if (!isloggedIn) {
      return 0;
    }
    return getProductCount(cartDetails, product_id);
  }, [cartDetails, isloggedIn, product_id, initialCount]);

  const disabledRemove = !user_permissions?.can_remove && count === 1;

  const handleAddToCart = useCallback(
    async (e) => {
      if (typeof customOnAddItem === 'function') {
        return customOnAddItem();
      }
      // Use current cart, or selected from dropdown
      const target_cart_id = typeof e === 'string' ? e : cart_id;

      if (!product_id) {
        return;
      }
      if (!isloggedIn) {
        setLoginModal(true);
        return;
      }
      try {
        let crossCartsCount = count;
        let details = cartDetails;

        if (cart_id !== target_cart_id) {
          // If not current cart was selected from dropdown:
          // - change cart
          // - show message that cart was changed
          // - fetch new cart details right now to get actual product quantity
          changeCart(target_cart_id);
          details = await getCartDetails({ cart_id: target_cart_id }).unwrap();
          m.success(`You have switched to "${details.cart_title}"`);
          crossCartsCount = getProductCount(details, product_id);
        }
        if (crossCartsCount === 0) {
          await addProduct({
            cart_id: target_cart_id,
            body: { product_id },
          }).unwrap();

          getFunc(onAfterAdd)();
          return;
        }
        const cartItem = details.items.find((item) => {
          return item.product.id === product_id;
        });
        const item_id = cartItem.id;

        if (item_id) {
          await updateProduct({
            item_id,
            cart_id: target_cart_id,
            body: {
              fund_id: '0',
              inventory_type: '0',
              qty: crossCartsCount + 1,
            },
          }).unwrap();
        }
      } catch (ex) {
        m.responseError(ex);
      }
    },
    [
      updateProduct,
      addProduct,
      cartDetails,
      cart_id,
      count,
      getCartDetails,
      isloggedIn,
      m,
      product_id,
      onAfterAdd,
      customOnAddItem,
    ],
  );

  const handleRemoveFromCart = useCallback(async () => {
    if (typeof customOnRemoveItem === 'function') {
      return customOnRemoveItem();
    }
    if (!isloggedIn) {
      setLoginModal(true);
      return;
    }
    if (disabledRemove) {
      // TODO: show something?
      return;
    }
    try {
      const cartItem = cartDetails.items.find((item) => {
        return item.product.id === product_id;
      });
      const item_id = cartItem.id;

      if (count > 1) {
        await updateProduct({
          item_id,
          cart_id,
          body: {
            fund_id: '0',
            inventory_type: '0',
            qty: count - 1,
          },
        }).unwrap();

        return;
      }
      await removeProduct({ cart_id, item_id }).unwrap();
    } catch (ex) {
      m.responseError(ex);
    }
  }, [
    customOnRemoveItem,
    isloggedIn,
    m,
    count,
    cartDetails,
    product_id,
    updateProduct,
    cart_id,
    removeProduct,
    disabledRemove,
  ]);

  const handleClickMenu = useCallback(
    (target_cart_id) => {
      handleAddToCart(target_cart_id);
      setCardsMenuEl(null);
    },
    [handleAddToCart],
  );

  const handleonChange = useCallback(
    (e) => {
      const re = /^\d+$/;
      const result = re.test(e.target.value);

      if (e.target.value !== '' && !result) return;

      if (typeof setCount === 'function') {
        setCount(e.target.value);
      }
    },
    [setCount],
  );

  const variantStyle = disabled ? 'outlined' : variant;

  return (
    <>
      <ModalLogin show={loginModal} setShow={setLoginModal} />

      <Box
        {...rest}
        width={width}
        className={cx(
          style.addButton,
          variantStyle === 'outlined' && style.outlined,
          disabled && style.disabled,
          className,
        )}
      >
        {count || inputField ? (
          <div className={cx(style.actions, containerClassName)}>
            <Button2
              fullWidth
              size={size}
              variant={variantStyle}
              loading={removeLoading}
              disabled={
                disabled ||
                (min && count <= min) ||
                disabledRemove ||
                removeLoading
              }
              onClick={handleRemoveFromCart}
              className={style.remove}
              {...buttonProps}
            >
              <Icon.Minus />
            </Button2>

            <div className={style.count}>
              {inputField ? (
                <TextField
                  className={style.field}
                  onChange={handleonChange}
                  color={'primary'}
                  value={count}
                  fullWidth={false}
                />
              ) : (
                count
              )}
            </div>

            <Button2
              variant={variantStyle}
              size={size}
              fullWidth
              onClick={handleAddToCart}
              disabled={
                disabled ||
                (max && count === max) ||
                disabledAdd ||
                removeLoading
              }
              className={style.add}
              {...buttonProps}
            >
              <Icon.Plus />
            </Button2>
          </div>
        ) : (
          <>
            {typeof renderAddButton === 'function' ? (
              renderAddButton({ setCardsMenuEl })
            ) : (
              <Button2
                className={cx(style.add, addButtonClassName)}
                variant={variant}
                size={size}
                fullWidth
                disabled={disabled}
                loading={addLoading}
                onClick={(e) => {
                  if (!isloggedIn) {
                    setLoginModal(true);
                    return;
                  }
                  setCardsMenuEl(e.currentTarget);
                }}
                {...buttonProps}
              >
                Add to Cart
              </Button2>
            )}

            <Menu
              anchorEl={cardsMenuEl}
              open={Boolean(cardsMenuEl)}
              className={style.menu}
              onClose={() => {
                setCardsMenuEl(null);
              }}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                style: {
                  minWidth: width,
                  maxHeight: 310,
                },
              }}
            >
              {customerCardsList?.map((card) => (
                <MenuItem
                  key={card.id}
                  selected={card.id === cart_id}
                  onClick={() => {
                    handleClickMenu(card.id);
                  }}
                >
                  {card.title}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </>
  );
};

export default AddToCardButton;
